import React from 'react';
import { AlertTriangle, Clock, Home, Truck } from 'lucide-react';
import { formatDriveMinutes, formatFleetClock, windowGarageReturn } from '../utils/fleetWindows';

function clockFromMs(ms, timezone) {
  if (ms == null || !Number.isFinite(ms)) return '';
  return formatFleetClock(new Date(ms).toISOString(), timezone);
}

export default function FleetWindowHeader({ window, driver, trips = [], timezone = 'America/Los_Angeles', compact = false }) {
  if (!window) {
    return (
      <div
        className={`flex items-center gap-2 rounded-md border-2 border-amber-400 bg-amber-50 text-amber-900 ${
          compact ? 'mt-2 mb-1 px-2.5 py-1.5' : 'mt-4 mb-2 px-3 py-2'
        }`}
        data-fleet-window="none"
      >
        <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
        <span className="text-[11px] font-black uppercase tracking-wide">No fleet window</span>
      </div>
    );
  }

  const rig = window.rig_no || window.fleet_id || 'Fleet';
  const los = [window.los_title, window.space_type_title ? `(${window.space_type_title})` : '']
    .filter(Boolean)
    .join(' ');
  const clock = `${formatFleetClock(window.start, timezone)}–${formatFleetClock(window.end, timezone)}`;
  const ret = windowGarageReturn(driver, window);
  const leave = clockFromMs(ret.leaveMs, timezone);
  const arrive = clockFromMs(ret.arriveMs, timezone);
  const hasReturn = ret.miles != null || ret.arriveMs != null;
  const awayLabel = ret.miles != null ? `${ret.miles.toFixed(1)} mi` : null;

  return (
    <div
      className={`rounded-md border-2 border-primary bg-primary-light text-main-dark ${
        compact ? 'mt-2 mb-1' : 'mt-4 mb-2'
      }`}
      data-fleet-window={window.window_index}
    >
      <div className={`flex flex-wrap items-center justify-between gap-2 border-b border-primary/30 ${compact ? 'px-2 py-1.5' : 'px-2.5 py-2'}`}>
        <div className="flex min-w-0 items-center gap-1.5 text-main-dark">
          <Truck className="h-3.5 w-3.5 shrink-0 text-main-primary" />
          <span className={`font-black uppercase tracking-wide text-main-dark ${compact ? 'text-[11px]' : 'text-xs'}`}>
            Rig {rig}
            {los ? <span className="font-bold text-main-secondary"> · {los}</span> : null}
          </span>
        </div>
        <span className="inline-flex items-center gap-1 shrink-0 text-[11px] font-black tabular-nums text-main-dark">
          <Clock className="h-3 w-3 text-main-primary" />
          {clock || '—'}
        </span>
      </div>

      <div className={`flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] font-bold text-main-dark ${compact ? 'px-2 py-1.5' : 'px-2.5 py-1.5'}`}>
        {hasReturn ? (
          <>
            <span className="inline-flex items-center gap-1" title="Backend last dropoff → garage (GIS when that check is on, otherwise haversine)">
              <Home className="h-3 w-3 text-main-primary" />
              Away {awayLabel}
              <span className="font-medium text-main-secondary">last dropoff → garage</span>
            </span>
            <span className="inline-flex items-center gap-1" title="Backend drive time from last dropoff to garage">
              <Clock className="h-3 w-3 text-main-primary" />
              ETD {formatDriveMinutes(ret.driveMinutes)}
              {leave ? <span className="font-medium text-main-secondary">leave {leave}</span> : null}
              {arrive ? <span className="font-medium text-main-secondary">at garage {arrive}</span> : null}
            </span>
            {ret.late ? (
              <span className="inline-flex items-center gap-1 text-amber-800">
                <AlertTriangle className="h-3 w-3" />
                After window end
              </span>
            ) : null}
          </>
        ) : (
          <span className="font-medium text-main-secondary">
            Garage return updates after the estimate runs.
          </span>
        )}
      </div>
    </div>
  );
}
